import Vue from 'vue'
import {mapGetters} from 'vuex'
import {userPositionMarker} from 'js/lib/planner'
import BillingPointsOfSalesService from 'js/services/api/billing/point-of-sales.js'

import MapMixin from 'js/mixins/map.js'
import VueBarMixin from 'js/mixins/vue-bar.js'

import MarkersMixin from './_markers_mixin'
import SidepanelMixin from './_sidepanel_mixin'

import template from './index.pug'

import {findIconColor} from '../../lib/constants/icon_pdv'

export default Vue.extend({
  name: 'pos-page',
  template: template(),
  mixins: [MapMixin, VueBarMixin, MarkersMixin, SidepanelMixin],
  data() {
    return {
      pointsOfSales: [],
      zoom: undefined,
      selectedTabKey: null,
      useDynamicTabs: true
    }
  },
  computed: {
    ...mapGetters(['userPosition']),
    ...mapGetters('administrativeArea', { 'businessTypes': 'getEnabledBusinessTypes' }),
    isBusinessTypeMenu() {
      if (!this.useDynamicTabs || !Array.isArray(this.businessTypes)) return false

      const key = this.selectedTabKey
      return this.businessTypes.includes(key) || key === 'others'
    },
    dynamicTabs() {
      if(this.useDynamicTabs && this.businessTypes?.length > 0) {
        const sorted = [...this.businessTypes].sort((a, b) => a.localeCompare(b))
        .map(type => ({
          key: type,
          text: type
        }))
        sorted.push({
          key: 'others',
          text: this.$t('pointOfSales.sidepanel.header.tabs.others')
        })

        return sorted
      } else {
        return [
          { key: 'Todas', text: this.$t('pointOfSales.sidepanel.header.tabs.all') },
          { key: 'rechargeOnly', text: this.$t('pointOfSales.sidepanel.header.tabs.rechargeOnly') },
          { key: 'adquisitionAndRecharge', text: this.$t('pointOfSales.sidepanel.header.tabs.acquisitionAndRecharge') },
          { key: 'additionalCapabilities', text: this.$t('pointOfSales.sidepanel.header.tabs.additionalCapabilities') },
        ]
      }
    }, 
    userIcon() {
      return userPositionMarker
    },
    /*
     * Filtra los puntos de venta
     */
   filteredPoints() {
      const key = this.selectedTabKey

     if (this.useDynamicTabs && this.businessTypes?.length > 0) {
       return this.pointsOfSales
     }

      switch (key) {
        case 'rechargeOnly':
          return this.pointsOfSales.filter(
            p => p.enrollment_capacity === 2 && !(p.capacity_code > 0)
          )
        case 'adquisitionAndRecharge':
          return this.pointsOfSales.filter(
            p => p.enrollment_capacity === 1
          )
        case 'additionalCapabilities':
          return this.pointsOfSales.filter(
            p => p.capacity_code > 0 || p.enrollment_capacity === 3
          )
        default:
          return this.pointsOfSales
      }
    },

    /*
     * Ordena los puntos de venta por distancia si el usuario ha dado permiso de ubicación
     * o alfabéticamente si no lo ha hecho
     */
    orderedPoints() {
      let filterForDraw = this.filteredPoints.sort(function(a, b) {
        return a.store_id - b.store_id;
      });

      filterForDraw.map(item => {
        let icon_color
        if ((item.enrollment_capacity.toString() === '3' || item.capacity_code > 0) && item.id) {
          icon_color = findIconColor(
            item.id,
            item.capacity_code,
            item.capacity_description,
            item.enrollment_capacity.toString()
          )
        }
        // devDump(icon_color, 'color');
        item.color = icon_color
      })
      return _.orderBy(filterForDraw, ['spot_name'], ['desc'])
    }
  },
  watch: {
    dynamicTabs: {
      immediate: true,
      handler(tabs) {
        if (tabs?.length) {
          const exists = tabs.some(t => t.key === this.selectedTabKey)
          if (!exists) {
            this.selectedTabKey = tabs[0].key
          }
        }
      }
    },
    $municipality: {
      handler(value) {
        if (value) { this.setMunicipalityConfig(value) }
      },
      immediate: true
    },

    administrativeArea() {
      this.loadInformation()
    },

    sidePanelOpen() {
      setTimeout(() => this.map.invalidateSize(true), 250)
    }
  },
  created() {
    this.loadInformation()
  },
  methods: {
    onTabChange(tab) {
      this.selectedTabKey = tab.key || tab

      if (this.useDynamicTabs && this.businessTypes?.length > 0) {
        this.getPointsOfSales()
      }
    },
    async loadInformation() {
      if (!this.administrativeArea) return

      await this.getPointsOfSales()
    },
    /*
     * Recupera los puntos de venta de la API
     */
    getPointsOfSales() {
      const params = {
        administrative_area_id: this.administrativeArea.id,
        municipality_id: this.$municipality.id,
        per: 0
      }

      const key = this.selectedTabKey

      if (this.useDynamicTabs && this.businessTypes?.length > 0) {
        if (key === 'others') {
          params.exclude_business_types = this.businessTypes.join(',')
        } else if (this.businessTypes.includes(key)) {
          params.business_type = key
        }
      }

      return BillingPointsOfSalesService.index(params).then(response => {
        this.pointsOfSales = response.data
      })
    },

    getPointOfSalesId(pointOfSales) {
      return `point-of-sales-${pointOfSales.id}`
    },

    degreesToRadians(degrees) {
      return degrees * Math.PI / 180
    },

    distanceInKm(lat1, lon1, lat2, lon2) {
      const earthRadiusKm = 6371

      const dLat = this.degreesToRadians(lat2 - lat1)
      const dLon = this.degreesToRadians(lon2 - lon1)

      lat1 = this.degreesToRadians(lat1)
      lat2 = this.degreesToRadians(lat2)

      const a = Math.sin(dLat / 2) * Math.sin(dLat / 2) +
        Math.sin(dLon / 2) * Math.sin(dLon / 2) * Math.cos(lat1) * Math.cos(lat2)
      const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a))
      return earthRadiusKm * c
    },

    /*
     * Calcula la distancia entre la posición del usuario y la de los puntos de venta
     */
    calculateDistances() {
      _.each(this.pointsOfSales, point => {
        point.distance = this.distanceInKm(
          this.userPosition.lat, this.userPosition.lng,
          point.position.lat, point.position.lng
        )
        point.distanceFormatted = point.distance.toFixed(2).replace('.', ',')
      })
    },

    setMunicipalityConfig(municipality) {
      this.zoom = municipality.stops_map_zoom
    }
  }
})
